import React from "react";
import { ClassNameProps, Column, Conditional, Else, If, makeClassName, Row, StyleProps } from "@hex-insights/core";
import {
	MediaAttachment,
	Post,
	SharingPolicy,
	useMediaAttachmentSocialItemsQuery,
	User,
	UserGroup,
	UserGroupNameAndColorFragment,
	UserNameAndImageFragment,
} from "../../../../Utilities";
import { MediaAttachmentUtilities } from "../../../Utilities";
import * as ModelComponents from "../../_General";
import * as MediaAttachmentComponents from "../../MediaAttachment";
import { SharingView, SimpleSharingView } from "../../SharingPolicy";
import * as SocialCommentComponents from "../../SocialComment";
import * as SocialReactionComponents from "../../SocialReaction";
import { AuthorTimestampView } from "../General";
import { PostUpdatePermissionRequired } from "../PermissionRequired";
import { ControlledPostUpdateModalController } from "../UpdateModal";
import styles from "./styles.module.css";

export type DetailDisplayPost = Pick<Post, "id" | "createdAt" | "title" | "body"> & {
	author: (Pick<User, "id"> & UserNameAndImageFragment) | null;
	mediaAttachments: Pick<
		Post["mediaAttachments"][0],
		"id" | "title" | "description" | "isFile" | "fileURL" | "fileKey" | "fileContentType" | "linkURL"
	>[];
	socialReactions: SocialReactionComponents.ReactionsViewProps["reactions"];
	socialComments: SocialCommentComponents.CommentsViewProps["comments"];
	sharingPolicy:
		| (Pick<SharingPolicy, "id" | "isPublic"> & {
				userGroups: (Pick<UserGroup, "id"> & UserGroupNameAndColorFragment)[];
		  })
		| null;
};

export type PostDetailDisplayProps = {
	post: DetailDisplayPost;
	isNew?: boolean;
	onClickVisualAttachment: (index: number) => void;
	withAudience?: boolean;
	editable?: boolean;
	withReactions?: boolean;
	withComments?: boolean;
	isPreview?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function PostDetailDisplay({
	post,
	isNew,
	onClickVisualAttachment,
	withAudience = false,
	editable = false,
	withReactions = false,
	withComments = false,
	isPreview = false,
	className,
	style,
}: PostDetailDisplayProps) {
	const viewerReaction = post?.socialReactions.find((reaction) => reaction.author?.isCurrent) ?? null;
	const postID = post?.id;
	const reactionFormValues = React.useMemo(() => ({ postID: postID ?? null }), [postID]);

	const { visualAttachments, fileAttachments, linkAttachments } = React.useMemo(
		() => MediaAttachmentUtilities.splitAndExtractMediaAttachments(post.mediaAttachments, post.body),
		[post.mediaAttachments, post.body],
	);

	const showSharing = !!post.sharingPolicy && (post.sharingPolicy.isPublic || post.sharingPolicy.userGroups.length > 0);

	return (
		<Column justify="spaced-start" verticalSpacing="1rem" className={className} style={style}>
			<Column justify="spaced-start" verticalSpacing="0.25rem">
				<Row justify="space-between" align="center" style={{ marginRight: "2.5rem" }}>
					<Row justify="spaced-start" align="center">
						<AuthorTimestampView post={post} isNew={isNew} />

						<If condition={editable && !isPreview}>
							<PostUpdatePermissionRequired isAuthor={post.author?.isCurrent ?? false}>
								<ControlledPostUpdateModalController postID={post.id} variant="link" size="small">
									Edit
								</ControlledPostUpdateModalController>
							</PostUpdatePermissionRequired>
						</If>
					</Row>

					<If condition={withReactions}>
						<SocialReactionComponents.SocialReactionToggleButton
							instanceID={viewerReaction?.id}
							formValues={reactionFormValues}
							iconSize={24}
						/>
					</If>
				</Row>

				<If condition={withAudience}>
					<SharingView postID={post.id} size="small" className={styles["detail-display__audience"]} />
				</If>
			</Column>

			<ModelComponents.ExpandableText lines={8} className={styles["post-detail-display__body-text"]}>
				{post.body}
			</ModelComponents.ExpandableText>

			{showSharing && post.sharingPolicy && (
				<SimpleSharingView sharingPolicy={post.sharingPolicy} size="small" style={{ minWidth: 0 }} />
			)}

			<If condition={visualAttachments.length > 0 || fileAttachments.length > 0 || linkAttachments.length > 0}>
				<Column justify="spaced-start" verticalSpacing="1rem">
					<If condition={visualAttachments.length > 0}>
						<MediaAttachmentComponents.VisualAttachmentsRow
							attachments={visualAttachments}
							onClickAttachment={onClickVisualAttachment}
						/>
					</If>
					<If condition={fileAttachments.length > 0}>
						<MediaAttachmentComponents.FileAttachmentsRow attachments={fileAttachments} />
					</If>
					<If condition={linkAttachments.length > 0}>
						<MediaAttachmentComponents.LinkAttachmentsRow attachments={linkAttachments} />
					</If>
				</Column>
			</If>

			<If condition={withReactions}>
				<SocialReactionComponents.ReactionsView postID={post.id} reactions={post.socialReactions} />
			</If>
			<If condition={withComments}>
				<SocialCommentComponents.CommentsView postID={post.id} comments={post.socialComments} />
			</If>
		</Column>
	);
}

export type PostVisualAttachmentDetailDisplayProps = {
	post: DetailDisplayPost;
	index: number;
	mediaAttachment: DetailDisplayPost["mediaAttachments"][0];
	preloadMediaAttachments?: MediaAttachmentComponents.VisualAttachmentCarouselProps["preloadMediaAttachments"];
	totalAttachments: number;
	incrementIndex: () => void;
	decrementIndex: () => void;
	withReactions?: boolean;
	withComments?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function PostVisualAttachmentDetailDisplay({
	post,
	index,
	mediaAttachment,
	preloadMediaAttachments,
	totalAttachments,
	incrementIndex,
	decrementIndex,
	withReactions = false,
	withComments = false,
	className,
	style,
}: PostVisualAttachmentDetailDisplayProps) {
	return (
		<div className={makeClassName(styles["post-visual-attachment-detail-display"], className)} style={style}>
			<div className={styles["post-visual-attachment-detail-display__layout"]}>
				<div className={styles["post-visual-attachment-detail-display__media-section"]}>
					<MediaAttachmentComponents.VisualAttachmentCarousel
						mediaAttachment={mediaAttachment}
						preloadMediaAttachments={preloadMediaAttachments}
						index={index}
						incrementIndex={incrementIndex}
						decrementIndex={decrementIndex}
						totalAttachments={totalAttachments}
						maxWidth={800}
						style={{ height: "100%" }}
					/>
				</div>

				<Column
					justify="spaced-start"
					verticalSpacing="1rem"
					className={styles["post-visual-attachment-detail-display__details-section"]}
				>
					<AuthorTimestampView post={post} />

					<If condition={!!post.title}>
						<strong>{post.title}</strong>
					</If>

					<ModelComponents.ExpandableText lines={8}>
						<Conditional>
							<If condition={mediaAttachment.description !== ""}>{mediaAttachment.description}</If>
							<Else>{post.body}</Else>
						</Conditional>
					</ModelComponents.ExpandableText>

					<If condition={withReactions || withComments}>
						<MediaAttachmentSocialView
							key={mediaAttachment.id}
							attachment={mediaAttachment}
							withReactions={withReactions}
							withComments={withComments}
						/>
					</If>
				</Column>
			</div>
		</div>
	);
}

type MediaAttachmentSocialViewProps = {
	attachment: Pick<MediaAttachment, "id" | "title" | "description">;
	withReactions?: boolean;
	withComments?: boolean;
};

function MediaAttachmentSocialView({
	attachment,
	withReactions = false,
	withComments = false,
}: MediaAttachmentSocialViewProps) {
	const { data: socialData } = useMediaAttachmentSocialItemsQuery({ variables: { id: attachment.id } });

	return (
		<React.Fragment>
			<If condition={withReactions}>
				<SocialReactionComponents.ReactionsView
					mediaAttachmentID={attachment.id}
					reactions={socialData?.mediaAttachment.socialReactions ?? []}
				/>
			</If>

			<If condition={withComments}>
				<SocialCommentComponents.CommentsView
					mediaAttachmentID={attachment.id}
					comments={socialData?.mediaAttachment.socialComments ?? []}
				/>
			</If>
		</React.Fragment>
	);
}
